import React from 'react';
import { Plus, Trash2, Edit2, Save, X, Upload, FileSpreadsheet, Mail } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Checkbox } from '../../components/ui/checkbox';
import { Badge } from '../../components/ui/badge';

const EmailsInput = ({ emails, onChange, prefix }) => {
  const list = emails?.length ? emails : [''];
  return (
    <div className="space-y-2">
      {list.map((email, idx) => (
        <div key={idx} className="flex gap-2">
          <div className="relative flex-1">
            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              type="email"
              placeholder={`Email ${idx + 1} (opcional)`}
              value={email}
              onChange={(e) => onChange(list.map((v, i) => (i === idx ? e.target.value : v)))}
              className="pl-9"
              data-testid={`${prefix}-email-input-${idx}`}
            />
          </div>
          {list.length > 1 && (
            <Button type="button" variant="outline" size="sm" onClick={() => onChange(list.filter((_, i) => i !== idx))} data-testid={`${prefix}-remove-email-${idx}`}>
              <X size={14} />
            </Button>
          )}
        </div>
      ))}
      <Button type="button" variant="ghost" size="sm" onClick={() => onChange([...list, ''])} data-testid={`${prefix}-add-email-btn`}>
        <Plus size={14} className="mr-1" /> Añadir email
      </Button>
    </div>
  );
};

/**
 * Pestana de gestion de clientes
 */
const ClientsTab = ({
  clientes,
  destinations = [],
  newCliente,
  onNewClienteChange,
  editingCliente,
  onEditingClienteChange,
  onAddCliente,
  onEditCliente,
  onUpdateCliente,
  onDeleteCliente,
  onCancelEdit,
  onToggleDestination,
  // Excel import
  fileInputRef,
  onImportExcel,
  isImporting,
  // Pagination
  currentPage,
  itemsPerPage,
  totalPages,
  onPageChange,
  paginationComponent: PaginationComponent,
}) => {
  const destNames = destinations.map(d => d.name || d);
  const paginatedClientes = clientes.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  return (
    <Card>
      <CardHeader>
        <CardTitle>Gestion de Clientes</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Nuevo Cliente */}
        <div className="border p-4 rounded-lg bg-blue-50">
          <h3 className="font-semibold mb-3">Nuevo Cliente</h3>
          <div className="space-y-3">
            <Input
              placeholder="Nombre del cliente"
              value={newCliente.name}
              onChange={(e) => onNewClienteChange({ ...newCliente, name: e.target.value })}
              data-testid="new-cliente-input"
            />
            <EmailsInput
              emails={newCliente.emails}
              onChange={(emails) => onNewClienteChange({ ...newCliente, emails })}
              prefix="new-cliente"
            />
            <div>
              <Label className="text-sm mb-2 block">Destinos Permitidos:</Label>
              <div className="flex flex-wrap gap-2">
                {destNames.map((dest) => (
                  <div key={dest} className="flex items-center space-x-2">
                    <Checkbox
                      id={`new-cliente-${dest}`}
                      checked={(newCliente.destinations || []).includes(dest)}
                      onCheckedChange={() => onToggleDestination(dest)}
                    />
                    <label htmlFor={`new-cliente-${dest}`} className="text-sm cursor-pointer">
                      {dest}
                    </label>
                  </div>
                ))}
              </div>
            </div>
            <Button onClick={onAddCliente} className="w-full" data-testid="add-cliente-btn">
              <Plus size={18} className="mr-1" />
              Anadir Cliente
            </Button>
          </div>
        </div>

        {/* Import Excel Section */}
        <div className="border p-4 rounded-lg bg-green-50">
          <h3 className="font-semibold mb-3 flex items-center gap-2">
            <FileSpreadsheet size={20} />
            Importar desde Excel
          </h3>
          <p className="text-sm text-gray-600 mb-3">
            Sube un archivo Excel con los clientes. El archivo debe tener:
          </p>
          <ul className="text-sm text-gray-600 mb-3 list-disc list-inside">
            <li><strong>Columna A:</strong> Nombre del cliente (obligatorio)</li>
            <li><strong>Columna B:</strong> Destinos separados por coma (opcional) - Ej: "ANMAR, GAIKER"</li>
            <li><strong>Columna C:</strong> Emails del cliente (opcional). Puedes poner varios separados por coma, punto y coma o salto de línea - Ej: "compras@cliente.com; calidad@cliente.com"</li>
          </ul>
          <div className="flex gap-2">
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,.xls"
              onChange={onImportExcel}
              className="hidden"
              id="excel-import"
              data-testid="excel-import-input"
            />
            <Button
              onClick={() => fileInputRef.current?.click()}
              disabled={isImporting}
              variant="outline"
              className="flex-1 bg-white"
              data-testid="import-excel-btn"
            >
              <Upload size={18} className="mr-2" />
              {isImporting ? 'Importando...' : 'Seleccionar archivo Excel'}
            </Button>
          </div>
        </div>

        {/* Lista de Clientes */}
        <div className="space-y-2">
          <div className="text-sm text-gray-600 mb-2">
            Mostrando {Math.min((currentPage - 1) * itemsPerPage + 1, clientes.length)} - {Math.min(currentPage * itemsPerPage, clientes.length)} de {clientes.length} clientes
          </div>

          {paginatedClientes.map((cliente) => {
            const isEditing = editingCliente && editingCliente.id === cliente.id;
            return (
              <div
                key={cliente.id}
                className="p-3 bg-gray-50 rounded-lg"
                data-testid={`cliente-${cliente.id}`}
              >
                {isEditing ? (
                  // Modo Edicion
                  <div className="space-y-3">
                    <Input
                      value={editingCliente.name}
                      onChange={(e) => onEditingClienteChange({ ...editingCliente, name: e.target.value })}
                      placeholder="Nombre del cliente"
                    />
                    <EmailsInput
                      emails={editingCliente.emails}
                      onChange={(emails) => onEditingClienteChange({ ...editingCliente, emails })}
                      prefix="edit-cliente"
                    />
                    <div>
                      <Label className="text-sm mb-2 block">Destinos Permitidos:</Label>
                      <div className="flex flex-wrap gap-2">
                        {destNames.map((dest) => (
                          <div key={dest} className="flex items-center space-x-2">
                            <Checkbox
                              id={`edit-cliente-${cliente.id}-${dest}`}
                              checked={(editingCliente.destinations || []).includes(dest)}
                              onCheckedChange={() => onToggleDestination(dest)}
                            />
                            <label htmlFor={`edit-cliente-${cliente.id}-${dest}`} className="text-sm cursor-pointer">
                              {dest}
                            </label>
                          </div>
                        ))}
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button onClick={onUpdateCliente} size="sm" className="flex-1" data-testid="update-cliente-btn">
                        <Save size={16} className="mr-1" />
                        Guardar
                      </Button>
                      <Button onClick={onCancelEdit} variant="outline" size="sm">
                        <X size={16} />
                      </Button>
                    </div>
                  </div>
                ) : (
                  // Modo Vista
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium">{cliente.name}</p>
                      {(cliente.emails || []).length > 0 && (
                        <p className="text-xs text-gray-500 flex items-center gap-1 mt-0.5 flex-wrap" data-testid={`cliente-emails-${cliente.id}`}>
                          <Mail size={12} /> {cliente.emails.join(' · ')}
                        </p>
                      )}
                      <div className="flex gap-1 mt-1">
                        {(cliente.destinations || []).map((dest) => (
                          <Badge key={dest} variant="secondary" className="text-xs">
                            {dest}
                          </Badge>
                        ))}
                        {(!cliente.destinations || cliente.destinations.length === 0) && (
                          <span className="text-xs text-gray-500 italic">Sin destinos asignados</span>
                        )}
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => onEditCliente(cliente)}
                        data-testid={`edit-cliente-${cliente.id}`}
                      >
                        <Edit2 size={16} />
                      </Button>
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={() => onDeleteCliente(cliente.id, cliente.name)}
                        data-testid={`delete-cliente-${cliente.id}`}
                      >
                        <Trash2 size={16} />
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}

          {/* Paginacion */}
          {PaginationComponent}
        </div>
      </CardContent>
    </Card>
  );
};

export default ClientsTab;
